import * as PIXI from "pixi.js-legacy";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../../framework/src/constants/identifiers";
import MadmaxPixiLayer from "../../framework/src/engine/pixi/MadmaxPixiLayer";
import PixiLayers from "../../framework/src/engine/pixi/PixiLayers";
import { pixiSetTimeoutAsync } from "../../framework/src/engine/pixi/pixiTimeout";
import { getRandomBoolean, getRandomFloat, getRandomInt } from "../../framework/src/helpers";
import { getSprite } from "../../framework/src/modules/Sprite";
import {
    EASE,
    getTween,
    getTweenAsync,
} from "../../framework/src/modules/Tween";
import { Frames } from "../Code/Frames";
import PixiAudio from "../../framework/src/engine/pixi/PixiAudio";

@injectable()
export default class IdleAnimation {
    layers: Record<string, MadmaxPixiLayer>;
    layer: MadmaxPixiLayer;

    screenEdgeRight: number;
    yRange: Array<number>;

    cloudTextures: Array<PIXI.Texture>;
    cloudSpeeds: Array<number>;
    cloudDelayTime: Array<number>;

    clouds: Array<PIXI.Sprite>;
    sea: PIXI.AnimatedSprite;
    light: PIXI.Sprite;
    stormSprites: Array<PIXI.Sprite>;

    doAnimateClouds: boolean = true;
    doAnimateLightning: boolean = true;
    lightningAnimationIsRunning: boolean = false; // -- check value for already running animation    
    searchLightningAnimIs_ON: boolean = false;
    searching_ON: boolean = true;

    audio: PixiAudio;

    searchingPosition: { min: number, max: number } = { min: 0, max: 280 };

    constructor(
        @inject(SERVICE_IDENTIFIER.EngineAppLayers) layers: PixiLayers,
        @inject(SERVICE_IDENTIFIER.EngineAudio) audio: PixiAudio,
    ) {
        // this.layers = layers.getLayer("idleAnim");//.getAllLayers();
        // this.layer = this.layers.idleAnim;

        this.audio = audio;
    }

    init() {
        let numberOfClouds = 10;
        let numberOfLightnings = 5;

        // sets the range of Y positions to spawn the clouds in
        this.yRange = [0, 350];//400];

        this.initAnimatedSea();

        // the light before the lightning strikes
        this.initLight();
        // the lightning after the light blinks
        this.initLightning(numberOfLightnings);
        this.initClouds(numberOfClouds);
    }

    run() {
        this.sea.play();
        this.runAnimationTicker();
    }

    getChildren() {
        //!!! does the array will be taken with  members of array without ...
        return [this.sea, this.light, ...this.clouds, ...this.stormSprites];
    }

    initClouds(noc: number) {
        // noc => number of clouds

        this.clouds = [];
        this.cloudDelayTime = [];
        this.cloudSpeeds = [];

        // sets the edge of the screeen where the clouds be "restarted"
        this.screenEdgeRight = 1280 + 600;

        this.cloudTextures = [
            PIXI.Texture.from("cloud_01"),
            PIXI.Texture.from("cloud_02"),
            PIXI.Texture.from("cloud_03"),
        ]

        // number of clouds (noc) and add them to the array with random "Y" position and EMPTY texture
        for (let i = 0; i < noc; i++) {
            let tempSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
            this.clouds.push(tempSprite);
            this.cloudSpeeds.push(1);
            this.cloudDelayTime.push(0);
            this.randomCloud(i);
        }
    }

    moveClouds() {

        for (let i = 0; i < this.clouds.length; i++) {

            if (this.cloudDelayTime[i] < 0) {
                this.clouds[i].x -= this.cloudSpeeds[i];
            } else {
                this.cloudDelayTime[i]--;
            }

            if (this.clouds[i].x <= -600) {
                this.randomCloud(i);
            }
        }
    }

    // randombly pick texture and y position for the cloud that already passed, and move it back to the right (screenEdgeRight)
    randomCloud(index: number) {        
        this.clouds[index].x = this.screenEdgeRight; //1880;
        this.clouds[index].y = getRandomInt(this.yRange[0], this.yRange[1]);
        this.clouds[index].texture = this.cloudTextures[getRandomInt(0, this.cloudTextures.length - 1)];
        this.clouds[index].pivot.y = this.clouds[index].height;        
        this.clouds[index].name = "Idle Animation Cloud no." + index + ",img." + this.clouds[index].texture.textureCacheIds[0];

        // moment speed between 3600 ~ 7200 frames => /60fps => 1 min ~ 2 mins
        // depending on the Y postion ( perspective distance , the speed of the cloud is affected ) (1880+600)        
        this.cloudSpeeds[index] = (this.screenEdgeRight + 600) / (getRandomInt(3600, 7200) * ((this.clouds[index].y / 100) + 1));
        this.cloudDelayTime[index] = getRandomInt(1, 60) * 60;
    }

    async initAnimatedSea() {
        this.sea = new PIXI.AnimatedSprite(Frames["sea"]);
        this.sea.name = "Idle Animation Sea";
        this.sea.animationSpeed = 0.3;
        this.sea.position.set(0, 450); // 505 default ?
        this.sea.loop = true;
        this.sea.stop();
    }

    /*
     *  !!! DIFFERENCE BETWEEN LIGHT(lighting) AND LIGHTNING !!!
     */
    initLight() {
        this.light = getSprite("storm_lighting_01.png", 640, 384);
        this.light.name = "Idle Animation Light";
        this.light.alpha = 0;
    }

    lightAnimation() {

        this.light.alpha = 0;
        getTweenAsync(this.light, { alpha: 1 }, 100, EASE.BOUN_IO).then(() => {
            getTweenAsync(this.light, { alpha: 0 }, 500, EASE.BOUN_IO); //ELAS_IO); 
        });
    }

    async initLightning(numberOfLightnings: number) {

        this.stormSprites = [];

        for (let i = 0; i < numberOfLightnings; i++) {

            let tempSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
            tempSprite.name = "Idle Animation Lightning " + i + " EMPTY";
            tempSprite.anchor.set(0.5, 0);

            this.getRandomLightningPosition(tempSprite, 400);
            tempSprite.alpha = 0;
            tempSprite.visible = false;
            this.stormSprites.push(tempSprite);
        }
    }

    async runAnimationTicker() {
        let minTime = 360;  // minimal time for random timing of lightning
        let maxTime = 600;  // maximal time for random timing of lightning
        let lightningTime = getRandomInt(minTime, maxTime); // get the random time within the min and max time
        let rngLightnings = getRandomInt(1, 2);
        /*
         *  add function in the ticker, set random lightningTime as the timer for ticks'
         *  have a this.doAnimateLightning as back up to turn on an off the function
         *  --- this would need a fix to remove function from ticker
         */
        PIXI.Ticker.shared.add((delta) => {
            // standard background lightning that runs randomly with delay
            if (this.doAnimateLightning) {
                if (lightningTime < 0) {

                    if (!this.lightningAnimationIsRunning) { // check if there is NOT another lightning animation already in progress
                        rngLightnings = getRandomInt(1, 2); // this.stormSprites.length); -- 5 is too much for idle lightnings ?
                        this.animateLightning(rngLightnings);
                    }

                    lightningTime = getRandomInt(minTime, maxTime);
                }

                lightningTime--;
            } else if (this.searchLightningAnimIs_ON) { 
                // search lightning animation
                if (this.searching_ON) {

                    this.searchingLightningAnimation();
                }
            } else {
                // placeholder for other possible animations        
            }

            // moving clouds animation, doAnimateClouds is there just in case..
            if (this.doAnimateClouds) {
                this.moveClouds();
            }
        });
    }

    /**
     * cycle through lightning array ( this.stormSprites ) , 
     * set number of lightnings by numberOfLightnings and tween each sprite (do the animation)
     * @param {number} numberOfLightnings   
     */
    async animateLightning(numberOfLightnings: number) {

        this.lightningAnimationIsRunning = true;
        let promisesList = [];

        for (let i = 0; i < numberOfLightnings; i++) {
            promisesList.push(await this.singleLightningAnimation(this.stormSprites[i], 400, 50));
            // this.stormSprites[i].name = "Idle Animation Lightning " + i + " " + this.stormSprites[i].texture.textureCacheIds[0];
        }

        await Promise.all(promisesList).then(() => {
            this.lightningAnimationIsRunning = false;
            promisesList.length = 0;
        });
    }

    changeTexture(spriteForChange: PIXI.Sprite, textureListName: string) {        
        let textureIndex = getRandomInt(0, this.stormSprites.length - 1);
        spriteForChange.texture = Frames[textureListName][textureIndex];
        // need in the future ?
        // spriteForChange.anchor.x = 0.5;
    }

    /**
     * generates random lightning position and should change the scale, for the given sprite , depending on generated position
     * @param spriteForChange - sprite to be changed it's position and 
     * @param randomYmax - MAXimal number for random generated position of the sprite on Y position
     * @param randomYmin - MINimal number for random generated position of the sprite on Y position
     * @param randomXmax - MAXimal number for random generated position of the sprite on X position
     * @param randomXmin - MINimal number for random generated position of the sprite on X position
     */

    getRandomLightningPosition(
        spriteForChange: PIXI.Sprite,
        randomYmax: number = 400,//100,
        randomYmin: number = 50,// 0
        randomXmax: number = 1000,
        randomXmin: number = 280,
    ) {

        /* if the y is below 0 ?
        if (randomYmin <0 || randomYmax < 0){
            randomYmin = 0 ;
            randomYmax = 24;        
        }
        */
       
        let x = getRandomInt(randomXmin, randomXmax);        
        let y = getRandomInt(randomYmin, randomYmax);

        let yBottom = getRandomInt(520, 600);
        let scaleNumber = ((yBottom - y) / spriteForChange.texture.height);
        let flipTexture = getRandomBoolean() ? -1 : 1;

        spriteForChange.position.set(x, y);
        spriteForChange.scale.set(scaleNumber * flipTexture, scaleNumber);
    }

    async bigWinLightningsAnimation(maxY: number = 400) {

        let promiseList = [];
        let minY = 0;
        if (maxY >= 50) {
            minY = maxY - 50;
        }

        for (let i = 0; i < this.stormSprites.length; i++) {
            let timeRNG = getRandomFloat(0.01, 0.1);//1);
            promiseList.push(await pixiSetTimeoutAsync(timeRNG));
            promiseList.push(this.singleLightningAnimation(this.stormSprites[i], maxY, minY));
            // this.stormSprites[i].name = "Idle Animation Lightning " + i + " " + this.stormSprites[i].texture.textureCacheIds[0];
        }
        await Promise.all(promiseList);
        promiseList.length = 0;
    }

    async soundCheck(n: number) {
        if (!this.audio.isPlaying("bwin_BigWin_Thunder") || n > 1) {
            this.audio.playTrack("bwin_BigWin_Thunder");
        }
    }

    async runBigWinLightning(numberOfRepeats: number = 3) {        
        this.doAnimateLightning = false;
        let gap = 400 / numberOfRepeats;

        do {
            await this.bigWinLightningsAnimation(numberOfRepeats * gap);
            this.soundCheck(numberOfRepeats);
            
            numberOfRepeats--;
        } while (numberOfRepeats > -1)
        
        this.doAnimateLightning = true;
    }

    /**
     * run the searching animation,
     * control the it with boolean, in ticker animation
     * @param {number} minX 
     * @param {number} maxX 
     * @param {number} offedge - to keep the position of the edges of the reel, default is 20
     */
    runSearchingAnimation(minX: number, maxX: number, offedge: number = 20) {
        this.doAnimateLightning = false;
        this.searchLightningAnimIs_ON = true;

        this.searchingPosition.min = minX + offedge;
        this.searchingPosition.max = maxX - offedge;
    }

    stopSearchingAnimation() {
        this.doAnimateLightning = true;
        this.searchLightningAnimIs_ON = false;
    }

    /*
     * the lightning animation during searching
     */
    async searchingLightningAnimation() {
        this.searching_ON = false;
        let promiseList = [];

        for (let x = 0; x < this.stormSprites.length; x++) {
            let timeRNG = getRandomFloat(0.1, 0.3);
            promiseList.push(await pixiSetTimeoutAsync(timeRNG).then(
                async () => {                
                    await this.singleLightningAnimation(this.stormSprites[x], 20, 0, this.searchingPosition.max, this.searchingPosition.min);                
                })
            );
        }
        await Promise.all(promiseList);
        this.searching_ON = true;
        promiseList.length = 0;
    }

    /**
     * single lightning animation 
     * @param {PIXI.Sprite} stormSprite - Sprite to use for animation / tweening , used from the array of sprite "stormSprites[]"
     * @param {number} yMaxPosition - set the y max and min position for the random generation of Y position (min max random range) for the sprite
     * @param {number} yMinPosition - !!!! max position is first
     * @param {number} xMaxPosition - set the x max and min position for the random generation of X position (min max random range) for the sprite
     * @param {number} xMinPosition - !!!! max position is first
     */
    async singleLightningAnimation(stormSprite: PIXI.Sprite, yMaxPosition: number = 400, yMinPosition: number = 50, xMaxPosition: number = 1000, xMinPosition: number = 280, tweenTime: number = 300) {

        if (!stormSprite.visible) {
            stormSprite.alpha = 0;
            stormSprite.visible = true;

            this.light.alpha = 0;
            this.light.visible = true;

            this.changeTexture(stormSprite, "lightning");
            this.getRandomLightningPosition(
                stormSprite,
                yMaxPosition,
                yMinPosition,
                xMaxPosition,
                xMinPosition,
            );

            if (getRandomBoolean()) {

                /*
                 * ugly manual way of doing this , since in original the each light have it's own lightning as child 
                 * THIS CODE MAY NEED IMPROVEMENT
                 */
                getTweenAsync(this.light, { alpha: 0.6 }, 80, EASE.LINEAR);
                await getTweenAsync(stormSprite, { alpha: 0.6 }, 80, EASE.LINEAR);
                this.changeTexture(stormSprite, "lightning");

                getTweenAsync(this.light, { alpha: 0.2 }, 80, EASE.LINEAR);
                await getTweenAsync(stormSprite, { alpha: 0.2 }, 80, EASE.LINEAR);                
                this.changeTexture(stormSprite, "lightning");

                getTweenAsync(this.light, { alpha: 1 }, 80, EASE.LINEAR);
                await getTweenAsync(stormSprite, { alpha: 1 }, 80, EASE.LINEAR);                
                this.changeTexture(stormSprite, "lightning");

                getTweenAsync(this.light, { alpha: 0 }, 300, EASE.LINEAR);
                await getTweenAsync(stormSprite, { alpha: 0 }, 300, EASE.LINEAR);              

            } else {
                getTweenAsync(this.light, { alpha: 1 }, 100, EASE.LINEAR);
                await getTweenAsync(stormSprite, { alpha: 1 }, 100, EASE.LINEAR);
                this.changeTexture(stormSprite, "lightning");

                getTweenAsync(this.light, { alpha: 0 }, 440, EASE.LINEAR);
                await getTweenAsync(stormSprite, { alpha: 0 }, 440, EASE.LINEAR);                
            }

            stormSprite.visible = false;
            stormSprite.name = "Idle Animation Lightning " + stormSprite.texture.textureCacheIds[0];                
        }
    }
}
